import { EmojiEvents, ExpandLess, ExpandMore, Groups, PeopleAlt, Search } from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Divider,
    InputAdornment,
    List,
    ListItemButton,
    Pagination,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Back from '../../bar';

const API = process.env.REACT_APP_API;

export default function ReferrerList() {
    const navi = useNavigate();

    const [referrers, setReferrers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [expanded, setExpanded] = useState('');

    useEffect(() => {
        fetchReferrers(page, search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search]);

    const fetchReferrers = (p, s) => {
        setLoading(true);
        const params = new URLSearchParams();
        params.append('page', p);
        if (s) params.append('search', s);
        axios
            .get(`${API}/franchise/student/referrers?${params.toString()}`)
            .then((res) => {
                if (res.data.status === true) {
                    setReferrers(res.data.data || []);
                    setTotalPages(res.data.pagination?.total_pages || 1);
                } else {
                    setReferrers([]);
                }
            })
            .catch(() => setReferrers([]))
            .finally(() => setLoading(false));
    };

    const handleSearchSubmit = (e) => {
        if (e.key === 'Enter') {
            setSearch(searchInput);
            setPage(1);
        }
    };

    const toggleExpand = (studentid) => {
        setExpanded((prev) => (prev === studentid ? '' : studentid));
    };

    const goToReferrerView = (referrerId, studentId) => {
        navi(`/controller/referrer/view?referrer_id=${referrerId}&student_id=${studentId}`);
    };

    return (
        <Box className='content'>
            <Container>
                <Back text='Referrers' />

                <TextField
                    fullWidth
                    size='small'
                    placeholder='Search by referrer name, roll no. or mobile...'
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchSubmit}
                    sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <Search fontSize='small' color='action' />
                            </InputAdornment>
                        ),
                    }}
                />

                {loading ? (
                    <Box textAlign='center' py={8}>
                        <CircularProgress />
                    </Box>
                ) : referrers.length === 0 ? (
                    <Alert severity='info'>No referrers found.</Alert>
                ) : (
                    <Stack spacing={2}>
                        {referrers.map((r) => {
                            const isOpen = expanded === r.studentid;
                            const initials = r.name
                                ? r.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
                                : '?';
                            return (
                                <Card key={r.studentid} sx={{ borderRadius: 3 }}>
                                    <CardContent sx={{ pb: '16px !important' }}>
                                        <Stack direction='row' alignItems='center' spacing={2}>
                                            <Avatar sx={{ bgcolor: '#0E3A43', fontWeight: 700 }}>{initials}</Avatar>
                                            <Box flexGrow={1} minWidth={0}>
                                                <Typography fontWeight={700} noWrap>{r.name}</Typography>
                                                <Stack direction='row' spacing={1} flexWrap='wrap' gap={0.5} mt={0.5}>
                                                    <Chip size='small' label={r.rollno || r.studentid} sx={{ fontSize: 11 }} />
                                                    {r.mobile && <Chip size='small' label={r.mobile} sx={{ fontSize: 11 }} />}
                                                    <Chip size='small' icon={<Groups sx={{ fontSize: '14px !important' }} />} label={`${r.referred_count} referred`} color='primary' variant='outlined' sx={{ fontSize: 11 }} />
                                                    <Chip size='small' icon={<EmojiEvents sx={{ fontSize: '14px !important', color: '#FFB300 !important' }} />} label={`${r.balance} coins`} sx={{ fontSize: 11, bgcolor: '#FFFDE7', fontWeight: 600 }} />
                                                </Stack>
                                            </Box>
                                            <Button
                                                size='small'
                                                variant='outlined'
                                                startIcon={<PeopleAlt fontSize='small' />}
                                                onClick={() => goToReferrerView(r.studentid, r.referred?.[0]?.studentid || r.studentid)}
                                            >
                                                Add Coins
                                            </Button>
                                            <Button size='small' onClick={() => toggleExpand(r.studentid)} endIcon={isOpen ? <ExpandLess /> : <ExpandMore />}>
                                                {isOpen ? 'Hide' : 'View'}
                                            </Button>
                                        </Stack>

                                        <Collapse in={isOpen} timeout='auto' unmountOnExit>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Typography fontSize={12} fontWeight={700} color='text.secondary' mb={0.5}>
                                                REFERRED STUDENTS
                                            </Typography>
                                            <List dense disablePadding>
                                                {r.referred.map((s) => (
                                                    <ListItemButton
                                                        key={s.studentid}
                                                        onClick={() => goToReferrerView(r.studentid, s.studentid)}
                                                        sx={{ borderRadius: 2, mb: 0.5 }}
                                                    >
                                                        <Stack direction='row' alignItems='center' justifyContent='space-between' width='100%'>
                                                            <Box>
                                                                <Typography fontSize={14} fontWeight={600}>{s.name}</Typography>
                                                                <Typography fontSize={12} color='text.secondary'>{s.rollno || s.studentid} · {s.mobile || '—'}</Typography>
                                                            </Box>
                                                            <Chip size='small' label={s.status} color={s.status === 'active' ? 'success' : 'warning'} sx={{ textTransform: 'capitalize', fontSize: 11 }} />
                                                        </Stack>
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Collapse>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}

                {totalPages > 1 && (
                    <Stack alignItems='center' mt={3}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, val) => setPage(val)}
                            color='primary'
                            shape='rounded'
                        />
                    </Stack>
                )}
            </Container>
        </Box>
    );
}
